import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams, Link } from 'react-router-dom';

function timeAgo(iso) {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1) return `${Math.floor(d * 24)}h ago`;
  if (d < 30) return `${Math.floor(d)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function RecentlyDeleted() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState({ events: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = () => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/events/recently-deleted`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setData(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenantSlug]);

  const restore = async (id) => {
    const res = await apiFetch(`/api/tenants/${tenantSlug}/events/${id}/restore`, { method: 'POST' });
    if (res.ok) load();
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Recently deleted</h1>
        <div className="text-sm text-muted-foreground">{data.count} events (30-day retention)</div>
      </div>
      {/* Mobile: cards (table is md+ only) so Restore is always reachable */}
      <div className="md:hidden space-y-2">
        {data.events.map((e) => (
          <div key={e.id} className="border border-border rounded-md p-3 bg-card text-sm">
            <div className="flex items-start gap-3">
              {e.cover_image_url ? <img src={e.cover_image_url} alt="" className="w-10 h-10 rounded object-cover bg-muted flex-shrink-0" /> : <div className="w-10 h-10 rounded bg-muted flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{e.talent}</div>
                <div className="text-xs text-muted-foreground">{new Date(e.date).toLocaleDateString()} · {e.status} · deleted {timeAgo(e.deleted_at)}</div>
              </div>
              <button onClick={() => restore(e.id)} className="px-2.5 py-1 text-xs border border-input rounded-md hover:bg-accent flex-shrink-0">Restore</button>
            </div>
          </div>
        ))}
        {data.events.length === 0 && (
          <div className="text-center text-muted-foreground py-6 text-sm">Nothing deleted in the last 30 days.</div>
        )}
      </div>

      <div className="border border-border rounded-md overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium w-14"></th>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Talent</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Deleted</th>
              <th className="px-3 py-2 font-medium w-24"></th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="px-2 py-1.5">
                  {e.cover_image_url ? <img src={e.cover_image_url} alt="" className="w-10 h-10 rounded object-cover bg-muted" /> : <div className="w-10 h-10 rounded bg-muted" />}
                </td>
                <td className="px-3 py-2">{new Date(e.date).toLocaleDateString()}</td>
                <td className="px-3 py-2">{e.talent}</td>
                <td className="px-3 py-2 text-muted-foreground">{e.status}</td>
                <td className="px-3 py-2 text-muted-foreground">{timeAgo(e.deleted_at)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => restore(e.id)} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Restore</button>
                </td>
              </tr>
            ))}
            {data.events.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Nothing deleted in the last 30 days.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-xs">
        <Link to={`/${tenantSlug}/list`} className="text-primary hover:underline">← Back to list</Link>
      </div>
    </div>
  );
}
